import React, { useState, useEffect } from 'react';
import formulaEngine from './utils/FormulaEngine';
import {
  extractFormulasFromStructures,
  loadMergedFormulasFromStorage,
  persistFormulasToStorage,
} from './utils/salaryComponentLibrarySync';
import { DsButton, DsInput } from '../../../../design-system';

const AdvancedFormulaManager = ({ 
  isOpen, 
  onClose, 
  savedComponents = [], 
  structures = [],
  organizationalLevels = {},
  statutoryRates = {},
  onFormulaSelect,
  selectedFormula = null 
}) => {
  const [formulas, setFormulas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFormulas, setFilteredFormulas] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [formulaData, setFormulaData] = useState({
    name: '',
    description: '',
    formula: '',
    category: 'earning', // earning, deduction, benefit, reimbursement
    isActive: true,
    variables: [],
    validationErrors: []
  });
  const [validationResult, setValidationResult] = useState(null);

  // Available formula functions
  const formulaFunctions = [
    { name: 'SUM', description: 'Sum of values', syntax: 'SUM(value1, value2, ...)' },
    { name: 'AVG', description: 'Average of values', syntax: 'AVG(value1, value2, ...)' },
    { name: 'MAX', description: 'Maximum value', syntax: 'MAX(value1, value2, ...)' },
    { name: 'MIN', description: 'Minimum value', syntax: 'MIN(value1, value2, ...)' },
    { name: 'ROUND', description: 'Round to decimal places', syntax: 'ROUND(value, decimals)' },
    { name: 'CEIL', description: 'Round up', syntax: 'CEIL(value)' },
    { name: 'FLOOR', description: 'Round down', syntax: 'FLOOR(value)' },
    { name: 'ABS', description: 'Absolute value', syntax: 'ABS(value)' },
    { name: 'IF', description: 'Conditional logic', syntax: 'IF(condition, true_value, false_value)' },
    { name: 'AND', description: 'Logical AND', syntax: 'AND(condition1, condition2, ...)' },
    { name: 'OR', description: 'Logical OR', syntax: 'OR(condition1, condition2, ...)' },
    { name: 'NOT', description: 'Logical NOT', syntax: 'NOT(condition)' }
  ];

  // Comparison symbols
  const comparisonSymbols = [
    { symbol: '==', description: 'Equal to' },
    { symbol: '!=', description: 'Not equal to' },
    { symbol: '>', description: 'Greater than' },
    { symbol: '<', description: 'Less than' },
    { symbol: '>=', description: 'Greater than or equal' },
    { symbol: '<=', description: 'Less than or equal' }
  ];

  // Operators
  const operators = [
    { symbol: '+', description: 'Addition' },
    { symbol: '-', description: 'Subtraction' },
    { symbol: '*', description: 'Multiplication' },
    { symbol: '/', description: 'Division' },
    { symbol: '%', description: 'Modulo' },
    { symbol: '^', description: 'Exponentiation' },
    { symbol: '(', description: 'Open parenthesis' },
    { symbol: ')', description: 'Close parenthesis' }
  ];

  // CTC (Cost to Company) - base component for salary structure, always available (from Employee.jsx)
  const CTC_BASE_COMPONENT = {
    id: 'ctc',
    name: 'CTC',
    displayLabel: 'CTC (Cost to Company)'
  };

  // Available components = CTC first (base), then saved components (avoid duplicate CTC by id/name)
  const availableComponents = [
    CTC_BASE_COMPONENT,
    ...(savedComponents || []).filter(
      (c) => String(c.id || '').toLowerCase() !== 'ctc' && String(c.name || '').toUpperCase() !== 'CTC'
    )
  ];

  // Statutory rates for formula building
  const statutoryOptions = [
    { name: 'PF_RATE', description: 'Provident Fund Rate', value: '12' },
    { name: 'ESI_RATE', description: 'ESI Rate', value: '1.75' },
    { name: 'GRATUITY_RATE', description: 'Gratuity Rate', value: '4.81' },
    { name: 'TDS_THRESHOLD', description: 'TDS Threshold', value: '250000' },
    { name: 'MAX_PF_LIMIT', description: 'Maximum PF Limit', value: '15000' }
  ];

  // Load saved formulas whenever the manager opens (storage + structure-embedded)
  useEffect(() => {
    if (!isOpen) return;
    try {
      const fromStorage = loadMergedFormulasFromStorage();
      const fromStructures = extractFormulasFromStructures(structures || []);
      const byId = new Map();
      const updatedMs = (f) => Date.parse(f?.updatedAt || "") || 0;
      [...fromStorage, ...fromStructures].forEach((f, idx) => {
        if (!f || typeof f !== "object") return;
        const id =
          f.id != null
            ? String(f.id)
            : `auto-${idx}-${f.name || ""}-${f.formula || ""}`;
        // Same formula in browser library and a structure copy → keep the most recently updated.
        const prev = byId.get(id);
        if (prev && updatedMs(prev) > updatedMs(f)) return;
        byId.set(id, { ...f, id });
      });
      const merged = Array.from(byId.values());
      setFormulas(merged);
      setFilteredFormulas(merged);
      if (merged.length) persistFormulasToStorage(merged);
    } catch (err) {
      console.error("Failed to load formulas", err);
      setFormulas([]);
      setFilteredFormulas([]);
    }
  }, [isOpen, structures]);

  // Filter formulas based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredFormulas(formulas);
    } else {
      const q = searchQuery.toLowerCase();
      const filtered = formulas.filter((formula) =>
        String(formula.name || "").toLowerCase().includes(q) ||
        String(formula.description || "").toLowerCase().includes(q) ||
        String(formula.formula || "").toLowerCase().includes(q)
      );
      setFilteredFormulas(filtered);
    }
  }, [searchQuery, formulas]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormulaData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormulaChange = (e) => {
    const formula = e.target.value;
    setFormulaData(prev => ({
      ...prev,
      formula: formula,
      variables: extractVariables(formula)
    }));
  };

  const extractVariables = (formula) => {
    // Use the formula engine to extract variables (availableComponents includes CTC base)
    return formulaEngine.extractVariables(formula, availableComponents, statutoryRates);
  };

  const validateFormula = () => {
    const { formula } = formulaData;

    // Use the formula engine for validation (pass availableComponents so CTC and saved components are valid)
    const validation = formulaEngine.validateFormula(formula, availableComponents, statutoryRates);
    const variables = extractVariables(formula);

    setValidationResult({
      isValid: validation.isValid,
      errors: validation.errors,
      variables: variables
    });

    return validation;
  };

  const handleSaveFormula = () => {
    const validation = validateFormula();
    if (!validation.isValid) {
      setFormulaData(prev => ({
        ...prev,
        validationErrors: validation.errors
      }));
      window.alert(
        `Formula is invalid:\n${(validation.errors || []).join('\n') || 'Check formula syntax and variables.'}`
      );
      return;
    }

    const newFormula = {
      id: editingFormula ? editingFormula.id : Date.now().toString(),
      ...formulaData,
      createdAt: editingFormula ? editingFormula.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    let updatedFormulas;
    if (editingFormula) {
      updatedFormulas = formulas.map(f => f.id === editingFormula.id ? newFormula : f);
    } else {
      updatedFormulas = [...formulas, newFormula];
    }

    setFormulas(updatedFormulas);
    setFilteredFormulas(updatedFormulas);
    persistFormulasToStorage(updatedFormulas);
    
    // Reset form
    setFormulaData({
      name: '',
      description: '',
      formula: '',
      category: 'earning',
      isActive: true,
      variables: [],
      validationErrors: []
    });
    setShowCreateForm(false);
    setEditingFormula(null);
    setValidationResult(null);
    window.alert(editingFormula ? 'Formula updated.' : 'Formula saved.');
  };

  const handleEditFormula = (formula) => {
    setEditingFormula(formula);
    setFormulaData({
      name: formula.name,
      description: formula.description,
      formula: formula.formula,
      category: formula.category,
      isActive: formula.isActive,
      variables: formula.variables || [],
      validationErrors: []
    });
    setShowCreateForm(true);
  };

  const handleDeleteFormula = (formulaId) => {
    if (window.confirm('Are you sure you want to delete this formula?')) {
      const updatedFormulas = formulas.filter(f => f.id !== formulaId);
      setFormulas(updatedFormulas);
      setFilteredFormulas(updatedFormulas);
      persistFormulasToStorage(updatedFormulas);
    }
  };

  const handleSelectFormula = (formula) => {
    if (onFormulaSelect) {
      onFormulaSelect(formula);
    }
  };

  const insertIntoFormula = (text) => {
    setFormulaData(prev => ({
      ...prev,
      formula: prev.formula + text
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[hsl(var(--ds-surface))] rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--ds-border))]">
          <div>
            <h2 className="text-base font-semibold text-[hsl(var(--ds-fg))]">Advanced Formula Manager</h2>
            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">Manage calculation formulas for salary components</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-muted-fg))] p-1"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4">
          {!showCreateForm ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <DsInput
                  type="text"
                  placeholder="Search formulas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1"
                />
                <DsButton
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={() => setShowCreateForm(true)}
                >
                  + Create Formula
                </DsButton>
              </div>

              <div className="max-h-[60vh] overflow-y-auto divide-y divide-[hsl(var(--ds-border))] border border-[hsl(var(--ds-border))] rounded-md">
                {filteredFormulas.length === 0 ? (
                  <p className="text-sm text-[hsl(var(--ds-muted-fg))] py-6 text-center">No formulas found</p>
                ) : (
                  filteredFormulas.map((formula) => (
                    <div
                      key={formula.id}
                      className={`flex items-center gap-2 px-3 py-1.5 text-sm ${
                        selectedFormula?.id === formula.id
                          ? "bg-[hsl(var(--ds-accent))]/30"
                          : "bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]"
                      }`}
                    >
                      <span className="font-medium text-[hsl(var(--ds-fg))] shrink-0 w-28 truncate" title={formula.name}>
                        {formula.name}
                      </span>
                      <span className="text-xs text-[hsl(var(--ds-muted-fg))] capitalize shrink-0 w-16">
                        {formula.category}
                      </span>
                      <code className="flex-1 min-w-0 px-2 py-0.5 border border-[hsl(var(--ds-border))] rounded text-xs font-mono bg-[hsl(var(--ds-muted))] truncate">
                        {formula.formula}
                      </code>
                      <div className="flex items-center gap-1 shrink-0">
                        {onFormulaSelect && (
                          <DsButton
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleSelectFormula(formula)}
                          >
                            Select
                          </DsButton>
                        )}
                        <DsButton
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditFormula(formula)}
                        >
                          Edit
                        </DsButton>
                        <DsButton
                          type="button"
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteFormula(formula.id)}
                        >
                          Delete
                        </DsButton>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">
                  {editingFormula ? "Edit Formula" : "Create Formula"}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingFormula(null);
                    setFormulaData({
                      name: "",
                      description: "",
                      formula: "",
                      category: "earning",
                      isActive: true,
                      variables: [],
                      validationErrors: [],
                    });
                  }}
                  className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-muted-fg))] p-1"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[hsl(var(--ds-muted-fg))] w-24 shrink-0">Name *</span>
                  <DsInput
                    type="text"
                    name="name"
                    value={formulaData.name}
                    onChange={handleInputChange}
                    className="flex-1 min-w-[10rem]"
                    placeholder="Formula name"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[hsl(var(--ds-muted-fg))] w-24 shrink-0">Category</span>
                  <select
                    name="category"
                    value={formulaData.category}
                    onChange={handleInputChange}
                    className="flex-1 min-w-[10rem] px-2 py-1 border border-[hsl(var(--ds-border))] rounded"
                  >
                    <option value="earning">Earning</option>
                    <option value="deduction">Deduction</option>
                    <option value="benefit">Benefit</option>
                    <option value="reimbursement">Reimbursement</option>
                  </select>
                </div>
                <div className="lg:col-span-2 flex flex-wrap items-center gap-2">
                  <span className="text-[hsl(var(--ds-muted-fg))] w-24 shrink-0">Description</span>
                  <DsInput
                    type="text"
                    name="description"
                    value={formulaData.description}
                    onChange={handleInputChange}
                    className="flex-1 min-w-[12rem]"
                    placeholder="Optional"
                  />
                </div>
                <div className="lg:col-span-2 flex flex-wrap items-start gap-2">
                  <span className="text-[hsl(var(--ds-muted-fg))] w-24 shrink-0 pt-1">Expression *</span>
                  <textarea
                    name="formula"
                    value={formulaData.formula}
                    onChange={handleFormulaChange}
                    rows={2}
                    className="flex-1 min-w-[12rem] px-2 py-1 border border-[hsl(var(--ds-border))] rounded font-mono text-xs"
                    placeholder="e.g. CTC*50% or Basic*40%"
                  />
                </div>
                {formulaData.validationErrors.length > 0 && (
                  <div className="lg:col-span-2 text-xs text-red-600">
                    {formulaData.validationErrors.join("; ")}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[hsl(var(--ds-muted-fg))] w-24 shrink-0">Active</span>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formulaData.isActive}
                      onChange={(e) =>
                        setFormulaData((prev) => ({ ...prev, isActive: e.target.checked }))
                      }
                      className="h-3.5 w-3.5 rounded"
                    />
                    Yes
                  </label>
                </div>
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  <DsButton
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={validateFormula}
                  >
                    Validate
                  </DsButton>
                  <DsButton
                    type="button"
                    size="sm"
                    variant="primary"
                    onClick={handleSaveFormula}
                  >
                    {editingFormula ? "Update" : "Save"}
                  </DsButton>
                </div>

                <div className="lg:col-span-2 border-t border-[hsl(var(--ds-border))] pt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                  <div>
                    <p className="text-xs text-[hsl(var(--ds-muted-fg))] mb-1">Components</p>
                    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                      {availableComponents.map((component) => (
                        <button
                          key={component.id}
                          type="button"
                          onClick={() => insertIntoFormula(component.name)}
                          className="px-2 py-0.5 text-xs border border-[hsl(var(--ds-border))] rounded hover:bg-[hsl(var(--ds-muted))]"
                        >
                          {component.displayLabel || component.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-[hsl(var(--ds-muted-fg))] mb-1">Operators</p>
                    <div className="flex flex-wrap gap-1">
                      {operators.map((op) => (
                        <button
                          key={op.symbol}
                          type="button"
                          onClick={() => insertIntoFormula(op.symbol)}
                          className="px-2 py-0.5 text-xs border border-[hsl(var(--ds-border))] rounded hover:bg-[hsl(var(--ds-muted))]"
                          title={op.description}
                        >
                          {op.symbol}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-[hsl(var(--ds-muted-fg))] mb-1">Functions</p>
                    <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                      {formulaFunctions.map((func) => (
                        <button
                          key={func.name}
                          type="button"
                          onClick={() => insertIntoFormula(func.syntax)}
                          className="px-2 py-0.5 text-xs border border-[hsl(var(--ds-border))] rounded hover:bg-[hsl(var(--ds-muted))]"
                          title={func.description}
                        >
                          {func.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-[hsl(var(--ds-muted-fg))] mb-1">Statutory / Compare</p>
                    <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                      {statutoryOptions.map((statutory) => (
                        <button
                          key={statutory.name}
                          type="button"
                          onClick={() => insertIntoFormula(statutory.name)}
                          className="px-2 py-0.5 text-xs border border-[hsl(var(--ds-border))] rounded hover:bg-[hsl(var(--ds-muted))]"
                        >
                          {statutory.name}
                        </button>
                      ))}
                      {comparisonSymbols.map((comp) => (
                        <button
                          key={comp.symbol}
                          type="button"
                          onClick={() => insertIntoFormula(comp.symbol)}
                          className="px-2 py-0.5 text-xs border border-[hsl(var(--ds-border))] rounded hover:bg-[hsl(var(--ds-muted))]"
                          title={comp.description}
                        >
                          {comp.symbol}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {validationResult && (
                  <div
                    className={`lg:col-span-2 text-xs px-2 py-1.5 rounded border ${
                      validationResult.isValid
                        ? "border-green-200 bg-green-50 text-green-800"
                        : "border-red-200 bg-red-50 text-red-800"
                    }`}
                  >
                    {validationResult.isValid
                      ? `Valid${
                          validationResult.variables?.length
                            ? ` · Variables: ${validationResult.variables.join(", ")}`
                            : ""
                        }`
                      : validationResult.errors?.join("; ")}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedFormulaManager;
